// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-mainframe.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-mainframe.ts — MAINFRAME '84's ICON SET, and the only
// RASTER-CONSTRUCTED set in the programme (R21).
//
// THE SHARED ID CONTRACT (same as every style set). A style does not get its
// own id space: it answers the SAME symbol ids the Blueprint sprite defines,
// so every call site in the app is unchanged and a style switch is a sprite
// swap, not a refactor. scripts/check-icon-refs.mjs enforces both halves with
// measured counts:
//
//   1. NO ORPHANS. Every id below must exist in the Blueprint sprite. An id
//      only this set defines would render for MAINFRAME and blank for
//      everyone else — the silent-blank failure the icons gate exists for.
//   2. TOTAL COVERAGE (owner correction 2026-09-07 — this REPLACES the old
//      "named, counted fallback is fine" rule this file used to state). This
//      set answers ALL 127 ids. It has to, and the reason is specific to this
//      style: styles/mainframe.css sets `.icon { fill: currentColor; stroke:
//      none }`, and every Blueprint glyph is pure OPEN-STROKE Lucide art with
//      no fill attribute anywhere in the sprite (measured: 0 `fill=` attrs).
//      A fallback under a fill-only rule does not render "in the wrong
//      style", it renders WRONG:
//        · `antenna` is four open line segments — zero enclosed area, so the
//          CHANNELS nav row drew NOTHING at all;
//        · `lighthouse`/`container`/`siren`/`badge-check` have closed outline
//          paths — they filled solid and read as unidentifiable blobs
//          (SCOPE, DEPLOYS, ESCALATIONS, GATES);
//        · `git-pull-request` is two <circle>s plus a path — the circles
//          filled, the path vanished, CHANGES read as two dots.
//      Nine nav/rail slots, silently, until the owner found them in
//      production. check-icon-refs.mjs now REFUSES any fallback for a style
//      with no dated COVERAGE_DEBT row, and this style has none.
//
// THE DRAWING RULES (spec §6). NO STROKES AT ALL. Every glyph is built from
// filled 2x2px cells on a 10x10 module inside the shared 24 viewBox —
// dot-matrix CHARACTER FORMATION, not a vector line redrawn with square caps.
// That is what separates it from a 2px stencil-cut steel set at a glance and
// at 16px: those glyphs are CUT, these are PRINTED. Right angles and 45°
// only; no curve is ever approximated with more than three cells, which is
// why every round form here is an OCTAGON and reads as one.
//
// The cell grid is the whole geometry system, so it is expressed as one:
// each glyph is ten ten-character rows, `#` for a filled cell. `cells()`
// merges each row's horizontal runs into a single <rect>, so a glyph costs a
// handful of nodes rather than a hundred, and the emitted coordinates are
// integers on a 2px lattice — which is what makes `shape-rendering:
// crispEdges` (styles.css, this style's `.icon` rule) produce a cell rather
// than a grey smear at 16px.
//
// Stroke weight is NOT set here and must not be: styles.css turns `.icon`
// into `fill: currentColor; stroke: none` under this style, so one rule
// governs the whole set and a glyph cannot opt out of the construction.

/** Cell size and origin inside the 24 viewBox: a 10x10 module of 2px cells,
 * inset 2px on every side. 2 + 10*2 + 2 = 24. */
const CELL = 2;
const ORIGIN = 2;
const MODULE = 10;

/**
 * Ten rows of ten characters -> `<rect>` markup, horizontal runs merged.
 *
 * Throws on a malformed grid rather than silently drawing a wrong glyph: a
 * 9-character row would shift every cell after it by half a cell and the
 * result would still render, which is exactly the class of defect that
 * survives review.
 */
export function cells(rows: string[]): string {
  if (rows.length !== MODULE) {
    throw new Error(`icons-mainframe: a glyph is ${MODULE} rows, got ${rows.length}`);
  }
  const out: string[] = [];
  for (let r = 0; r < MODULE; r++) {
    const row = rows[r] ?? '';
    if (row.length !== MODULE) {
      throw new Error(`icons-mainframe: row ${r} is ${row.length} cells, expected ${MODULE}`);
    }
    let c = 0;
    while (c < MODULE) {
      if (row[c] !== '#') { c++; continue; }
      let run = 0;
      while (c + run < MODULE && row[c + run] === '#') run++;
      out.push(
        `<rect x="${ORIGIN + c * CELL}" y="${ORIGIN + r * CELL}" ` +
          `width="${run * CELL}" height="${CELL}" />`,
      );
      c += run;
    }
  }
  return out.join('');
}

/** THE PROMPT — MAINFRAME's brand mark, and the artwork behind both `helm`
 * and `ship-wheel` (Blueprint draws those with the same ship-wheel path, so
 * the set keeps them identical to each other too). A blocky `>` caret
 * followed by a SOLID BLOCK CURSOR, sitting on a one-cell baseline rule. The
 * mark IS the app's punctuation under this style: the same block is the
 * empty-state tail, the streaming indicator and the attention chip, all
 * driven by one timing token (styles.css `--mf-blink`). */
const PROMPT = cells([
  '..........',
  '..........',
  '.##.......',
  '..##......',
  '...##.....',
  '....##....',
  '...##.....',
  '..##..####',
  '.##...####',
  '##########',
]);

/** The octagonal status ring — three cells per corner, which is the curve
 * budget. Every rung of the ladder is this ring with one mark inside it, so
 * the ladder reads as one family at 16px and the MARK carries the rank. */
const RING_ROWS = [
  '..######..',
  '.##....##.',
  '##......##',
  '#........#',
  '#........#',
  '#........#',
  '##......##',
  '.##....##.',
  '..######..',
  '..........',
];

/** Overlay marks onto the ring rows: `[row, col]` pairs, 0-indexed. */
function ring(marks: Array<[number, number]>): string {
  const rows = RING_ROWS.map((r) => r.split(''));
  for (const [r, c] of marks) {
    const row = rows[r];
    if (!row || c < 0 || c >= MODULE) throw new Error(`icons-mainframe: ring mark out of range ${r},${c}`);
    row[c] = '#';
  }
  return cells(rows.map((r) => r.join('')));
}

/**
 * id -> symbol INNER markup (the `<symbol>` wrapper and the viewBox are
 * added by icons.ts, so a set cannot get either wrong).
 *
 * WHAT THIS SET RE-CUTS: ALL 127. The first 30 (through `circle-check`) were
 * the original cut — the marks the eye lands on most at the sizes where a
 * silhouette actually reads: the machine family, the instrument family, the
 * whole status ladder, the mode pair, and the four glyphs that repeat per
 * row. The 97 below them close the set, in the same construction and drawn
 * to the same rules, because under this style's fill-only `.icon` rule a
 * Blueprint fallback is not a downgrade, it is a blank or a blob (see the
 * file header). Nothing here may be deleted without drawing a replacement:
 * the icons gate refuses this style at 126/127.
 */
export const MAINFRAME_SYMBOLS: Record<string, string> = {
  // ---- the prompt (brand) ---------------------------------------------
  helm: PROMPT,
  'ship-wheel': PROMPT,

  // ---- the machine family ----------------------------------------------
  terminal: cells([
    '##########',
    '#........#',
    '#.##.....#',
    '#..##....#',
    '#.##.....#',
    '#........#',
    '#..#####.#',
    '#........#',
    '#........#',
    '##########',
  ]),
  'square-terminal': cells([
    '##########',
    '#........#',
    '#.##.....#',
    '#..##....#',
    '#.##.....#',
    '#........#',
    '#..#####.#',
    '#........#',
    '#........#',
    '##########',
  ]),
  server: cells([
    '..........',
    '##########',
    '#........#',
    '#.#......#',
    '##########',
    '..........',
    '##########',
    '#.#......#',
    '#........#',
    '##########',
  ]),
  database: cells([
    '..######..',
    '.##....##.',
    '..######..',
    '.#......#.',
    '.#......#.',
    '..######..',
    '.#......#.',
    '.#......#.',
    '..######..',
    '..........',
  ]),
  box: cells([
    '##########',
    '#........#',
    '#........#',
    '##########',
    '#........#',
    '#........#',
    '#........#',
    '#........#',
    '##########',
    '..........',
  ]),
  layers: cells([
    '..........',
    '...####...',
    '.########.',
    '...####...',
    '..........',
    '.########.',
    '..........',
    '.########.',
    '..........',
    '..........',
  ]),

  // ---- the instrument family -------------------------------------------
  gauge: cells([
    '..........',
    '...####...',
    '.##....##.',
    '#........#',
    '#...##...#',
    '#..##....#',
    '#.##.....#',
    '#........#',
    '..........',
    '..........',
  ]),
  activity: cells([
    '..........',
    '..........',
    '......##..',
    '......##..',
    '##....##..',
    '.##..##.##',
    '..####....',
    '..........',
    '..........',
    '..........',
  ]),
  chart: cells([
    '..........',
    '.......##.',
    '.......##.',
    '....##.##.',
    '....##.##.',
    '.##.##.##.',
    '.##.##.##.',
    '.##.##.##.',
    '##########',
    '..........',
  ]),
  'grid-2x2': cells([
    '..........',
    '.####.####',
    '.#..#.#..#',
    '.#..#.#..#',
    '.####.####',
    '..........',
    '.####.####',
    '.#..#.#..#',
    '.#..#.#..#',
    '.####.####',
  ]),
  eye: cells([
    '..........',
    '..........',
    '..######..',
    '.##....##.',
    '##..##..##',
    '##..##..##',
    '.##....##.',
    '..######..',
    '..........',
    '..........',
  ]),
  bell: cells([
    '....##....',
    '...####...',
    '..######..',
    '..######..',
    '.########.',
    '.########.',
    '##########',
    '..........',
    '....##....',
    '..........',
  ]),
  search: cells([
    '..####....',
    '.##..##...',
    '##....##..',
    '##....##..',
    '.##..##...',
    '..####....',
    '....####..',
    '......####',
    '........##',
    '..........',
  ]),

  // ---- the four glyphs that repeat per row ------------------------------
  plus: cells([
    '..........',
    '....##....',
    '....##....',
    '....##....',
    '##########',
    '##########',
    '....##....',
    '....##....',
    '....##....',
    '..........',
  ]),
  x: cells([
    '..........',
    '.##.....##',
    '..##...##.',
    '...##.##..',
    '....###...',
    '....###...',
    '...##.##..',
    '..##...##.',
    '.##.....##',
    '..........',
  ]),
  'chevron-down': cells([
    '..........',
    '..........',
    '##......##',
    '.##....##.',
    '..##..##..',
    '...####...',
    '....##....',
    '..........',
    '..........',
    '..........',
  ]),
  'chevron-up': cells([
    '..........',
    '..........',
    '..........',
    '....##....',
    '...####...',
    '..##..##..',
    '.##....##.',
    '##......##',
    '..........',
    '..........',
  ]),

  // ---- the mode pair ----------------------------------------------------
  sun: cells([
    '....##....',
    '#...##...#',
    '.#.####.#.',
    '..######..',
    '##.####.##',
    '..######..',
    '.#.####.#.',
    '#...##...#',
    '....##....',
    '..........',
  ]),
  moon: cells([
    '...####...',
    '.##....##.',
    '##......##',
    '##........',
    '##........',
    '##........',
    '##......##',
    '.##....##.',
    '...####...',
    '..........',
  ]),

  // ---- the status ladder: one ring, seven states ------------------------
  'st-ring': ring([]),
  'st-half': cells([
    '..######..',
    '.##....##.',
    '##...#####',
    '#....#####',
    '#....#####',
    '#....#####',
    '##...#####',
    '.##....##.',
    '..######..',
    '..........',
  ]),
  'st-check': ring([[3, 7], [4, 2], [4, 6], [5, 3], [5, 5], [6, 4]]),
  'st-cross': ring([[2, 2], [2, 6], [3, 3], [3, 5], [4, 4], [5, 3], [5, 5], [6, 2], [6, 6]]),
  'st-pause': ring([[2, 3], [2, 4], [2, 6], [2, 7], [3, 3], [3, 4], [3, 6], [3, 7],
    [4, 3], [4, 4], [4, 6], [4, 7], [5, 3], [5, 4], [5, 6], [5, 7], [6, 3], [6, 4], [6, 6], [6, 7]]),
  'st-dash': cells([
    '..##..##..',
    '.#......#.',
    '#........#',
    '..........',
    '#........#',
    '..........',
    '#........#',
    '.#......#.',
    '..##..##..',
    '..........',
  ]),
  'st-seal': ring([[2, 3], [2, 4], [2, 5], [2, 6], [3, 2], [3, 3], [3, 6], [3, 7],
    [4, 2], [4, 7], [5, 2], [5, 3], [5, 6], [5, 7], [6, 3], [6, 4], [6, 5], [6, 6]]),

  // ---- the two intent marks that are not in the ladder ------------------
  'circle-alert': ring([[2, 4], [3, 4], [4, 4], [6, 4]]),
  'circle-check': ring([[3, 7], [4, 2], [4, 6], [5, 3], [5, 5], [6, 4]]),
  // ---- the nautical/brand family — the ids the rail and the corpus tree land on ---
  'circle-help': ring([[2, 3], [2, 4], [2, 5], [3, 6], [4, 4], [4, 5], [5, 4], [7, 4]]),
  assistant: cells([
    '....##....',
    '....##....',
    '..#.##.#..',
    '...####...',
    '##########',
    '...####...',
    '..#.##.#..',
    '....##....',
    '....##....',
    '..........',
  ]),
  lighthouse: cells([
    '...####...',
    '..######..',
    '.#.####.#.',
    '...####...',
    '...#..#...',
    '..#....#..',
    '..#....#..',
    '.#......#.',
    '##########',
    '..........',
  ]),
  compass: ring([[2, 6], [3, 5], [4, 4], [4, 5], [5, 4], [6, 3]]),
  dividers: cells([
    '....##....',
    '....##....',
    '...#..#...',
    '...#..#...',
    '..#....#..',
    '..#....#..',
    '.#......#.',
    '.#......#.',
    '##......##',
    '..........',
  ]),
  anchor: cells([
    '....##....',
    '...####...',
    '....##....',
    '..######..',
    '....##....',
    '#...##...#',
    '#...##...#',
    '##..##..##',
    '..######..',
    '..........',
  ]),
  ship: cells([
    '....##....',
    '....##....',
    '....####..',
    '....######',
    '....##....',
    '....##....',
    '..........',
    '##########',
    '.########.',
    '..######..',
  ]),
  cargo: cells([
    '..........',
    '##########',
    '#........#',
    '#.##..##.#',
    '#..####..#',
    '#..####..#',
    '#.##..##.#',
    '#........#',
    '##########',
    '..........',
  ]),
  porthole: cells([
    '..######..',
    '.##....##.',
    '##.####.##',
    '#.##..##.#',
    '#.#....#.#',
    '#.##..##.#',
    '##.####.##',
    '.##....##.',
    '..######..',
    '..........',
  ]),
  knot: cells([
    '..........',
    '..####....',
    '.##..##...',
    '.##..##...',
    '..######..',
    '...##..##.',
    '...##..##.',
    '....####..',
    '..........',
    '..........',
  ]),
  logbook: cells([
    '..........',
    '.########.',
    '.##.....#.',
    '.##.####.#',
    '.##.....#.',
    '.##.####.#',
    '.##.....#.',
    '.########.',
    '..........',
    '..........',
  ]),
  horizon: cells([
    '..........',
    '..........',
    '...####...',
    '..######..',
    '.########.',
    '..........',
    '##########',
    '..........',
    '##..##..##',
    '..........',
  ]),
  flag: cells([
    '##........',
    '##.#####..',
    '##.#####..',
    '##.#####..',
    '##.#####..',
    '##........',
    '##........',
    '##........',
    '##........',
    '..........',
  ]),
  buoy: cells([
    '....##....',
    '....##....',
    '...####...',
    '..######..',
    '.########.',
    '.########.',
    '..######..',
    '..........',
    '##########',
    '..........',
  ]),
  telescope: cells([
    '.......###',
    '......###.',
    '.....###..',
    '....###...',
    '...###....',
    '..###.....',
    '.###......',
    '..#.......',
    '.###......',
    '..........',
  ]),
  crane: cells([
    '##########',
    '...#...##.',
    '...#...##.',
    '...#..####',
    '...#......',
    '...#......',
    '...#......',
    '...#......',
    '.#####....',
    '..........',
  ]),
  antenna: cells([
    '..........',
    '#..#..#..#',
    '.#.#..#.#.',
    '..##..##..',
    '....##....',
    '....##....',
    '....##....',
    '...####...',
    '..######..',
    '..........',
  ]),
  gate: cells([
    '..........',
    '##......##',
    '##......##',
    '##########',
    '##......##',
    '##......##',
    '##########',
    '##......##',
    '##......##',
    '..........',
  ]),
  crew: cells([
    '..##..##..',
    '..##..##..',
    '..........',
    '.####.####',
    '.####.####',
    '.####.####',
    '.####.####',
    '.#..#.#..#',
    '.#..#.#..#',
    '..........',
  ]),
  gear: cells([
    '..#....#..',
    '.###..###.',
    '.########.',
    '###....###',
    '##......##',
    '##......##',
    '###....###',
    '.########.',
    '.###..###.',
    '..#....#..',
  ]),
  book: cells([
    '.#########',
    '.##......#',
    '.##......#',
    '.##......#',
    '.##......#',
    '.##......#',
    '.##......#',
    '.#########',
    '..########',
    '..........',
  ]),
  'book-open': cells([
    '..........',
    '.###..###.',
    '##.#..#.##',
    '##.#..#.##',
    '##.#..#.##',
    '##.#..#.##',
    '##.#..#.##',
    '.########.',
    '..........',
    '..........',
  ]),
  container: cells([
    '..........',
    '##########',
    '#........#',
    '##########',
    '#..#..#..#',
    '#..#..#..#',
    '#..#..#..#',
    '#..#..#..#',
    '##########',
    '..........',
  ]),
  scroll: cells([
    '..........',
    '.########.',
    '##......##',
    '.########.',
    '.#......#.',
    '.#.####.#.',
    '.#......#.',
    '.########.',
    '##......##',
    '.########.',
  ]),
  wave: cells([
    '..........',
    '..........',
    '.##....##.',
    '#..#..#..#',
    '....##....',
    '..........',
    '.##....##.',
    '#..#..#..#',
    '....##....',
    '..........',
  ]),
  signal: cells([
    '........##',
    '........##',
    '.....##.##',
    '.....##.##',
    '..##.##.##',
    '..##.##.##',
    '..##.##.##',
    '..........',
    '..........',
    '..........',
  ]),
  chev: cells([
    '..........',
    '..##......',
    '...##.....',
    '....##....',
    '.....##...',
    '......##..',
    '.....##...',
    '....##....',
    '...##.....',
    '..##......',
  ]),
  play: cells([
    '..##......',
    '..####....',
    '..######..',
    '..########',
    '..########',
    '..######..',
    '..####....',
    '..##......',
    '..........',
    '..........',
  ]),
  sonar: cells([
    '..######..',
    '.#......#.',
    '#..####..#',
    '#.#....#.#',
    '#.#.##.#.#',
    '#.#....#.#',
    '#..####..#',
    '.#......#.',
    '..######..',
    '..........',
  ]),
  tack: cells([
    '..######..',
    '..#....#..',
    '..#....#..',
    '.########.',
    '....##....',
    '....##....',
    '....##....',
    '.....#....',
    '.....#....',
    '..........',
  ]),
  shield: cells([
    '..######..',
    '.########.',
    '##########',
    '##########',
    '##########',
    '.########.',
    '..######..',
    '...####...',
    '....##....',
    '..........',
  ]),

  // ---- the record types (corpus objects + registers) -----------------------
  'scroll-text': cells([
    '..........',
    '.########.',
    '##......##',
    '.#.####.#.',
    '.#......#.',
    '.#.####.#.',
    '.#......#.',
    '.#.###..#.',
    '.########.',
    '..........',
  ]),
  bug: cells([
    '#........#',
    '.#.####.#.',
    '..######..',
    '###....###',
    '..######..',
    '###....###',
    '..######..',
    '.#.####.#.',
    '#........#',
    '..........',
  ]),
  'git-pull-request': cells([
    '.###..###.',
    '.#.#..#.#.',
    '.###..###.',
    '..#....#..',
    '..#....#..',
    '..#....#..',
    '..#....#..',
    '.###..###.',
    '.#.#..#.#.',
    '.###..###.',
  ]),
  siren: cells([
    '#...##...#',
    '.#.####.#.',
    '..######..',
    '.########.',
    '.########.',
    '##########',
    '..........',
    '##########',
    '..........',
    '..........',
  ]),
  'clipboard-check': cells([
    '...####...',
    '.########.',
    '.#......#.',
    '.#.....#.#',
    '.#....#..#',
    '.#.#.#...#',
    '.#..#....#',
    '.#......#.',
    '.########.',
    '..........',
  ]),
  'clipboard-list': cells([
    '...####...',
    '.########.',
    '.#......#.',
    '.#.#.##.#.',
    '.#......#.',
    '.#.#.##.#.',
    '.#......#.',
    '.#.#.##.#.',
    '.########.',
    '..........',
  ]),
  map: cells([
    '..........',
    '.########.',
    '.#..##..#.',
    '.#.####.#.',
    '.#.####.#.',
    '.#..##..#.',
    '.#..##..#.',
    '.#......#.',
    '.########.',
    '..........',
  ]),
  microscope: cells([
    '.....####.',
    '....##..##',
    '....##..##',
    '.....####.',
    '......##..',
    '....######',
    '..######..',
    '....##....',
    '##########',
    '..........',
  ]),
  workflow: cells([
    '####......',
    '#..#......',
    '####......',
    '..#.......',
    '..#####...',
    '......#...',
    '......####',
    '......#..#',
    '......####',
    '..........',
  ]),
  'notebook-tabs': cells([
    '..........',
    '.#######..',
    '.#.....###',
    '.#.....#..',
    '.#.....###',
    '.#.....#..',
    '.#.....###',
    '.#.....#..',
    '.#######..',
    '..........',
  ]),
  library: cells([
    '..........',
    '##.....##.',
    '##.##..##.',
    '##.##..##.',
    '##.##.###.',
    '##.##.###.',
    '##.##.###.',
    '##.##.###.',
    '##########',
    '..........',
  ]),

  // ---- the work verbs (task graph + engineer domains) ----------------------
  hammer: cells([
    '..........',
    '.########.',
    '.##....##.',
    '.########.',
    '....##....',
    '....##....',
    '....##....',
    '....##....',
    '....##....',
    '..........',
  ]),
  'flask-conical': cells([
    '...####...',
    '....##....',
    '....##....',
    '...####...',
    '...####...',
    '..######..',
    '.###..###.',
    '.########.',
    '##########',
    '..........',
  ]),
  stethoscope: cells([
    '.##....##.',
    '.##....##.',
    '..#....#..',
    '..#....#..',
    '..######..',
    '....##....',
    '....##....',
    '...####...',
    '...####...',
    '..........',
  ]),
  scale: cells([
    '....##....',
    '.########.',
    '.#..##..#.',
    '###.##.###',
    '.#..##..#.',
    '....##....',
    '....##....',
    '..######..',
    '.########.',
    '..........',
  ]),
  'git-merge': cells([
    '.##....##.',
    '.##....##.',
    '..#...##..',
    '..#..##...',
    '..#.##....',
    '..###.....',
    '..#.......',
    '.###......',
    '.###......',
    '..........',
  ]),
  'git-branch': cells([
    '.##.......',
    '.##....##.',
    '..#....##.',
    '..#....##.',
    '..#####...',
    '..#.......',
    '..#.......',
    '.###......',
    '.###......',
    '..........',
  ]),
  wrench: cells([
    '......####',
    '.....##..#',
    '.....#..##',
    '......####',
    '....###...',
    '..###.....',
    '.###......',
    '.##.......',
    '##........',
    '..........',
  ]),
  'badge-check': cells([
    '.#.####.#.',
    '.########.',
    '##......##',
    '#.....##.#',
    '#....##..#',
    '#.#.##...#',
    '#.###....#',
    '##......##',
    '.########.',
    '.#.####.#.',
  ]),

  // ---- the chrome verbs (toolbars, rail feet, menus) -----------------------
  'rotate-cw': cells([
    '..####.##.',
    '.##....###',
    '##......##',
    '#.........',
    '#.........',
    '#.........',
    '##......##',
    '.##....##.',
    '..######..',
    '..........',
  ]),
  'refresh-cw': cells([
    '.######.##',
    '##....####',
    '#........#',
    '#.........',
    '..........',
    '.........#',
    '#........#',
    '####....##',
    '##.######.',
    '..........',
  ]),
  'list-restart': cells([
    '##.#######',
    '..........',
    '##.#######',
    '..........',
    '##.#######',
    '..........',
    '.####.....',
    '##..#.....',
    '##........',
    '.####.....',
  ]),
  house: cells([
    '....##....',
    '...####...',
    '..######..',
    '.########.',
    '##########',
    '.##....##.',
    '.##.##.##.',
    '.##.##.##.',
    '.########.',
    '..........',
  ]),
  hourglass: cells([
    '##########',
    '.########.',
    '..######..',
    '...####...',
    '....##....',
    '...####...',
    '..######..',
    '.########.',
    '##########',
    '..........',
  ]),
  puzzle: cells([
    '.######...',
    '.#....#...',
    '.#....####',
    '.#.......#',
    '.#.......#',
    '.#....####',
    '.#....#...',
    '.#.####...',
    '.##..##...',
    '..........',
  ]),
  'chevrons-right': cells([
    '..........',
    '#....#....',
    '##...##...',
    '.##...##..',
    '..##...##.',
    '.##...##..',
    '##...##...',
    '#....#....',
    '..........',
    '..........',
  ]),
  'chevron-left': cells([
    '..........',
    '......##..',
    '.....##...',
    '....##....',
    '...##.....',
    '...##.....',
    '....##....',
    '.....##...',
    '......##..',
    '..........',
  ]),
  pin: cells([
    '..######..',
    '.##....##.',
    '##......##',
    '##.####.##',
    '##.####.##',
    '.##....##.',
    '..######..',
    '....##....',
    '....##....',
    '..........',
  ]),
  copy: cells([
    '..........',
    '..#######.',
    '..#.....#.',
    '..#.....#.',
    '#######.#.',
    '#.....#.#.',
    '#.....#.#.',
    '#.....####',
    '#######...',
    '..........',
  ]),
  'panels-top-left': cells([
    '..........',
    '##########',
    '##########',
    '##.......#',
    '##.......#',
    '##.......#',
    '##.......#',
    '##.......#',
    '##########',
    '..........',
  ]),
  ellipsis: cells([
    '..........',
    '..........',
    '..........',
    '..........',
    '##..##..##',
    '##..##..##',
    '..........',
    '..........',
    '..........',
    '..........',
  ]),
  'circle-dot': ring([[3, 4], [3, 5], [4, 4], [4, 5]]),
  'circle-x': ring([[2, 2], [2, 7], [3, 3], [3, 6], [4, 4], [4, 5], [5, 3], [5, 6], [6, 2], [6, 7]]),
  ban: ring([[2, 6], [2, 7], [3, 5], [3, 6], [4, 4], [4, 5], [5, 3], [5, 4], [6, 2], [6, 3]]),
  clock: ring([[3, 4], [4, 4], [4, 5], [4, 6]]),
  history: ring([[2, 4], [3, 4], [4, 2], [4, 3], [4, 4]]),
  'circle-arrow-up': ring([
    [2, 4], [2, 5], [3, 3], [3, 4], [3, 5], [3, 6], [4, 4], [4, 5], [5, 4], [5, 5], [6, 4], [6, 5]
  ]),

  // ---- arrows + transfer ---------------------------------------------------
  'arrow-up-right': cells([
    '...#######',
    '.......###',
    '......####',
    '.....##.##',
    '....##..##',
    '...##.....',
    '..##......',
    '.##.......',
    '##........',
    '..........',
  ]),
  'arrow-left': cells([
    '..........',
    '....#.....',
    '...##.....',
    '..###.....',
    '##########',
    '##########',
    '..###.....',
    '...##.....',
    '....#.....',
    '..........',
  ]),
  'arrow-right': cells([
    '..........',
    '.....#....',
    '.....##...',
    '.....###..',
    '##########',
    '##########',
    '.....###..',
    '.....##...',
    '.....#....',
    '..........',
  ]),
  download: cells([
    '....##....',
    '....##....',
    '....##....',
    '....##....',
    '##..##..##',
    '.##.##.##.',
    '..######..',
    '...####...',
    '##########',
    '..........',
  ]),
  upload: cells([
    '...####...',
    '..######..',
    '.##.##.##.',
    '##..##..##',
    '....##....',
    '....##....',
    '....##....',
    '....##....',
    '##########',
    '..........',
  ]),
  send: cells([
    '........##',
    '......####',
    '....##.###',
    '..##...###',
    '##.....###',
    '..#####...',
    '..###.....',
    '..#.......',
    '..........',
    '..........',
  ]),
  'log-in': cells([
    '..........',
    '......####',
    '......#..#',
    '...#..#..#',
    '..##..#..#',
    '#####.#..#',
    '..##..#..#',
    '...#..#..#',
    '......####',
    '..........',
  ]),
  'log-out': cells([
    '..........',
    '####......',
    '#..#......',
    '#..#..#...',
    '#..#..##..',
    '#..#.#####',
    '#..#..##..',
    '#..#..#...',
    '####......',
    '..........',
  ]),
  'external-link': cells([
    '......####',
    '.......###',
    '......#..#',
    '########..',
    '#......#..',
    '#......#..',
    '#......#..',
    '#......#..',
    '########..',
    '..........',
  ]),
  'undo-2': cells([
    '..........',
    '..#.......',
    '.##.......',
    '#########.',
    '.##.....##',
    '..#.....##',
    '........##',
    '.......##.',
    '..#####...',
    '..........',
  ]),
  reply: cells([
    '..........',
    '..#.......',
    '.##.......',
    '#########.',
    '.##.....#.',
    '..#.....#.',
    '........#.',
    '........#.',
    '.....####.',
    '..........',
  ]),
  'maximize-2': cells([
    '.....#####',
    '.....#####',
    '......###.',
    '.....##.##',
    '....##....',
    '....##....',
    '##.##.....',
    '.###......',
    '#####.....',
    '#####.....',
  ]),
  'minimize-2': cells([
    '#####.....',
    '#####.....',
    '.###......',
    '##.##.....',
    '....##....',
    '....##....',
    '.....##.##',
    '......###.',
    '.....#####',
    '.....#####',
  ]),

  // ---- editing + objects ---------------------------------------------------
  star: cells([
    '....##....',
    '....##....',
    '...####...',
    '##########',
    '.########.',
    '..######..',
    '..######..',
    '.##....##.',
    '##......##',
    '..........',
  ]),
  'folder-open': cells([
    '..........',
    '###.......',
    '#..#######',
    '#........#',
    '#........#',
    '#........#',
    '.#########',
    '..########',
    '...#######',
    '..........',
  ]),
  'file-pen': cells([
    '.#####....',
    '.#...##...',
    '.#....#...',
    '.#....#.##',
    '.#.....###',
    '.#....###.',
    '.#...###..',
    '.#..###...',
    '.######...',
    '..........',
  ]),
  'user-plus': cells([
    '..####..#.',
    '.##..##.#.',
    '.##..#####',
    '..####..#.',
    '........#.',
    '..######..',
    '.########.',
    '##########',
    '##########',
    '..........',
  ]),
  camera: cells([
    '..........',
    '...##.....',
    '##########',
    '#........#',
    '#..####..#',
    '#.##..##.#',
    '#..####..#',
    '#........#',
    '##########',
    '..........',
  ]),
  mail: cells([
    '..........',
    '##########',
    '##......##',
    '#.##..##.#',
    '#..####..#',
    '#........#',
    '#........#',
    '#........#',
    '##########',
    '..........',
  ]),
  'trash-2': cells([
    '...####...',
    '##########',
    '..........',
    '.########.',
    '.#.#..#.#.',
    '.#.#..#.#.',
    '.#.#..#.#.',
    '.#.#..#.#.',
    '.########.',
    '..........',
  ]),
  pencil: cells([
    '.......###',
    '......####',
    '.....#####',
    '....####..',
    '...####...',
    '..####....',
    '.####.....',
    '####......',
    '##........',
    '..........',
  ]),
  check: cells([
    '..........',
    '........##',
    '.......##.',
    '......##..',
    '##...##...',
    '.##.##....',
    '..###.....',
    '...#......',
    '..........',
    '..........',
  ]),
  square: cells([
    '..........',
    '##########',
    '#........#',
    '#........#',
    '#........#',
    '#........#',
    '#........#',
    '#........#',
    '##########',
    '..........',
  ]),
  filter: cells([
    '##########',
    '.########.',
    '..######..',
    '...####...',
    '....##....',
    '....##....',
    '....##....',
    '...####...',
    '...####...',
    '..........',
  ]),
  bookmark: cells([
    '.########.',
    '.#......#.',
    '.#......#.',
    '.#......#.',
    '.#......#.',
    '.#......#.',
    '.#......#.',
    '.#.####.#.',
    '.##.##.##.',
    '.###..###.',
  ]),
  link: cells([
    '..........',
    '..........',
    '####..####',
    '#..#..#..#',
    '#..####..#',
    '#..####..#',
    '#..#..#..#',
    '####..####',
    '..........',
    '..........',
  ]),
  'message-square': cells([
    '..........',
    '##########',
    '#........#',
    '#........#',
    '#........#',
    '#........#',
    '#........#',
    '#####.####',
    '..##......',
    '..#.......',
  ]),
  'square-pen': cells([
    '.......###',
    '......####',
    '#####.###.',
    '#....###..',
    '#...###..#',
    '#..###...#',
    '#........#',
    '#........#',
    '##########',
    '..........',
  ]),
  zap: cells([
    '.....###..',
    '....###...',
    '...###....',
    '..######..',
    '.....###..',
    '....###...',
    '...###....',
    '..###.....',
    '.###......',
    '..........',
  ]),


  // ── release-scope SECTION TYPES (#810 PR 4): one per renderer family,
  // cut in this set's own construction so the corpus TOC's section rows are
  // own-set under every style (icons:check refuses a fallback).
  'rs-prose': cells([
    '..########',
    '..#......#',
    '..#.####.#',
    '..#......#',
    '..#.####.#',
    '..#......#',
    '..#.##...#',
    '..#......#',
    '..########',
    '..........',
  ]),
  'rs-paired-lists': cells([
    '####..####',
    '#..#..#..#',
    '#.##..#.##',
    '#..#..#..#',
    '#.##..#.##',
    '#..#..#..#',
    '#.##..#.##',
    '#..#..#..#',
    '####..####',
    '..........',
  ]),
  'rs-table': cells([
    '##########',
    '#........#',
    '##########',
    '#....#...#',
    '#....#...#',
    '##########',
    '#....#...#',
    '#....#...#',
    '##########',
    '..........',
  ]),
  'rs-table-expansion': cells([
    '##########',
    '#........#',
    '##########',
    '#....#...#',
    '##########',
    '....#.....',
    '...###....',
    '.########.',
    '.#......#.',
    '.########.',
  ]),
  'rs-rules': cells([
    '..........',
    '##..######',
    '##........',
    '..........',
    '##..######',
    '##........',
    '..........',
    '##..######',
    '##........',
    '..........',
  ]),
  'rs-state-machine': cells([
    '..........',
    '####..####',
    '#..#..#..#',
    '#..#..#..#',
    '#..####..#',
    '#..#..#..#',
    '#..#.##..#',
    '#..#..#..#',
    '####..####',
    '..........',
  ]),
  'rs-spec-cards': cells([
    '########..',
    '#......#..',
    '#.########',
    '#.#......#',
    '#.#.####.#',
    '#.#......#',
    '###.####.#',
    '..#......#',
    '..########',
    '..........',
  ]),
  'rs-wireframe': cells([
    '##########',
    '#........#',
    '##########',
    '#........#',
    '#.####...#',
    '#.#..#.#.#',
    '#.#..#...#',
    '#.####.#.#',
    '#........#',
    '##########',
  ]),
};

/** Every id this set re-cuts. Sorted so the gate's output is stable. */
export const MAINFRAME_ICON_IDS: string[] = Object.keys(MAINFRAME_SYMBOLS).sort();
